'use client'

import { Avatar } from '@repo/ui/Avatar'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { ClockIcon } from '@repo/ui/ClockIcon'
import { ComingUpOverflowMenu } from '@repo/dashboard/ComingUpOverflowMenu'
import { formatDate, dateIsInThePast, dateIsToday } from '@repo/utils/time'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useTheme } from '@mui/material/styles'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import MUITooltip from '@mui/material/Tooltip'
import PropTypes from 'prop-types'

export const ComingUpItemCardContent = ({ item }) => {
  const theme = useTheme()
  const { toggleKeyDatesDrawer } = useKeyDateStore()
  const { setMatterDrawerIsOpen } = useMattersStore()

  const {
    upcomingDateType,
    clientNo,
    matterNo,
    matterDescription,
    feeEarnerDescription,
    feeEarnerId,
    keyDate,
    keyDateNotes,
    matterReviewDate,
    keyDateTypeDescription,
    inferredMatterReviewDate,
    isUserRestricted
  } = item || {}

  const date = formatDate(keyDate || inferredMatterReviewDate || matterReviewDate, 'EEE d MMM')
  const isOverdue = dateIsInThePast(keyDate || inferredMatterReviewDate || matterReviewDate)
  const isToday = dateIsToday(keyDate || inferredMatterReviewDate || matterReviewDate)
  const getHighlightColor = () => {
    if (isToday) return theme?.palette?.warningLight?.main || '#DE6A1E'
    if (isOverdue) return theme?.palette?.error?.main || '#C33241'
    return theme?.palette?.success?.main || 'black'
  }

  const highlightColor = getHighlightColor()

  const handleClick = () => {
    if (isUserRestricted) return

    if (upcomingDateType === 'KeyDate' && item?.keyDateId) {
      toggleKeyDatesDrawer({
        keyDatesDrawerIsOpen: true,
        keyDatesDrawerItemId: item.keyDateId,
        keyDatesDrawerMode: 'view'
      })
    } else if (upcomingDateType === 'Matter' && item?.clientNo) {
      setMatterDrawerIsOpen(true, item.clientNo, `${item.clientNo}-${item.matterNo}`)
    }
  }

  return (
    <MUITooltip arrow placement="top" title={isUserRestricted ? 'You are not authorised to access this matter' : ''}>
      <MUIStack
        data-testid="ComingUpItemCardContent"
        onClick={handleClick}
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        sx={{
          padding: '6px 8px 6px 16px',
          cursor: isUserRestricted ? 'not-allowed' : 'pointer',
          width: '100%',
          borderLeft: `6px solid ${highlightColor}`
        }}
      >
        <Avatar
          feeEarnerId={feeEarnerId}
          name={feeEarnerDescription}
          size="32px"
          sx={{ marginRight: 4, width: '32px' }}
        />

        <MUIStack
          direction="column"
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginRight: '15px', flex: 1, minWidth: 0 }}
        >
          {upcomingDateType === 'Matter' ? (
            <MUITypography variant="h6" sx={{ mb: 1, userSelect: 'none', pointerEvents: 'none', width: '100%' }} noWrap>
              Matter review
            </MUITypography>
          ) : (
            <MUITypography variant="h6" sx={{ mb: 1, userSelect: 'none', pointerEvents: 'none', width: '100%' }} noWrap>
              {keyDateTypeDescription}
              {keyDateNotes ? `: ${keyDateNotes}` : null}
            </MUITypography>
          )}

          <MUIStack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ minWidth: 0, flex: 1, width: '100%' }}
          >
            <ClientMatterDetailsPill
              clientNo={clientNo}
              matterNo={matterNo}
              sx={{ width: 'auto' }}
              isUserRestricted={isUserRestricted}
            />

            <MUITypography
              variant="body2"
              sx={{
                mb: 1,
                marginLeft: '6px',
                fontWeight: 500,
                color: 'text.secondary',
                pointerEvents: 'none',
                width: '100%',
                flex: 1
              }}
              noWrap
            >
              {matterDescription}
            </MUITypography>
          </MUIStack>
        </MUIStack>

        <MUIStack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ marginRight: 1, minWidth: '85px' }}
        >
          <ClockIcon sx={{ fontSize: '14px', marginRight: 1, color: highlightColor }} />
          <MUITypography
            variant="body2"
            sx={{
              mb: 0,
              color: highlightColor,
              userSelect: 'none',
              cursor: 'pointer',
              ml: 1
            }}
          >
            {date}
          </MUITypography>
        </MUIStack>

        <ComingUpOverflowMenu item={item} />
      </MUIStack>
    </MUITooltip>
  )
}

ComingUpItemCardContent.propTypes = {
  item: PropTypes.object
}
